import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, FileText, Globe, Loader2, MapPin, RefreshCw, Save, Store, Upload, X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLES } from "@/lib/roles";
import { useToast } from "@/components/ui/toast";
import { api } from "@/lib/api";
import { emailIssue, nameIssue, phoneIssue } from "@/lib/profileValidation";
import type { BusinessListing, BusinessListingCreate } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";

// Warm brand charcoal for the always-dark listing preview card (keeps white
// text + gold price legible in both themes — mirrors the dark showcase look).
const NAVY  = "#1c1710";
const NAVY2 = "#3a2c12";
const GOLD  = "#E8A020";
const GREEN = "#16A34A";
const RED   = "#CC3333";

const selectCls =
  "h-10 w-full rounded-xl border border-border/70 bg-white/80 px-3 py-2 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary/50 disabled:opacity-50";

const fmtCompact = (v: string): string => {
  const n = Number(v);
  if (!n) return "";
  if (n >= 1_000_000_000) return `$${+(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `$${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `$${+(n / 1_000).toFixed(1)}K`;
  return `$${n}`;
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const parsedAskingPrice = (val: string): number | null => {
  if (!val.trim()) return null;
  const n = Number(val.replace(/[$,\s]/g, ""));
  return !isNaN(n) && val.replace(/[$,\s]/g, "") !== "" ? n : null;
};
const isMakeOfferText = (val: string): boolean =>
  !!val.trim() && parsedAskingPrice(val) === null;

/** Subset of a Coming Soon lead used to pre-populate this form when publishing. */
interface LeadPrefill {
  id: string;
  title: string | null;
  website_url: string | null;
  owner_email: string | null;
  business_description: string | null;
  industry_category_id: number | null;
  country_id: number | null;
  state_id: number | null;
  city_id: number | null;
  city_raw: string | null;
  image_url: string | null;
}

export default function CreateListingPage() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const routerState = routerLocation.state as {
    leadPrefill?: LeadPrefill;
    advisorChecklist?: {
      owner_connected: boolean;
      due_diligence_completed: boolean;
      nda_signed: boolean;
      notes: string | null;
    };
  } | null;
  const leadPrefill = routerState?.leadPrefill ?? null;
  // Pre-publish due-diligence checklist answers carried over from the Coming Soon
  // page. Recorded to the new listing's audit log on creation.
  const advisorChecklist = routerState?.advisorChecklist ?? null;
  // When set, the source Coming Soon lead is removed once the listing is created.
  const [sourceLeadId, setSourceLeadId] = useState<string | null>(null);

  const isSeller      = !!user && hasRole("seller");
  // Both admin tiers: an org_admin's listing is published immediately by the
  // API too, and they pick sellers and write the public description like any admin.
  const isAdmin        = !!user && hasRole(...ADMIN_ROLES);
  const isAdminOrAgent = !!user && (hasRole(...ADMIN_ROLES) || hasRole("bl_advisor"));

  const [loading, setLoading] = useState(false);
  const [coverImageData, setCoverImageData] = useState<string | null>(null);   // AI-generated URL
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);          // User-uploaded file
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [imageOptions, setImageOptions] = useState<string[]>([]);
  const [imageGenerating, setImageGenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  const [title, setTitle] = useState("");
  const [overview, setOverview] = useState("");
  const [displayOverview, setDisplayOverview] = useState("");
  const [askingPrice, setAskingPrice] = useState("");
  const [revenue, setRevenue] = useState("");
  const [ebitda, setEbitda] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [cityName, setCityName] = useState("");
  const [countryId, setCountryId] = useState("");
  const [industryCategoryId, setIndustryCategoryId] = useState("");
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [establishedYear, setEstablishedYear] = useState("");
  const [establishedMonth, setEstablishedMonth] = useState("");
  const [isSbaApproved, setIsSbaApproved] = useState(false);
  const [isSellerFinancing, setIsSellerFinancing] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [sellerName, setSellerName] = useState(isSeller ? `${user.first_name} ${user.last_name}`.trim() : "");
  const [sellerEmail, setSellerEmail] = useState(isSeller ? user.email : "");
  const [sellerPhone, setSellerPhone] = useState(isSeller ? (user.phone || "") : "");
  const [sellers, setSellers] = useState<{ id: string; first_name: string; last_name: string; email: string; phone: string | null }[]>([]);
  const [selectedSellerId, setSelectedSellerId] = useState("");

  // ── PII detection ─────────────────────────────────────────────────────────
  // Scans buyer-visible free-text fields for phone numbers, email addresses,
  // company name (title), and seller name. Returns per-field violation messages.
  const PII_PHONE  = /(\+?1[\s.\-]?)?(\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4})/;
  const PII_EMAIL  = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/;

  const detectPii = (text: string, companyName: string, ownerName: string): string | null => {
    if (!text.trim()) return null;
    if (PII_PHONE.test(text)) return "Phone numbers are not allowed in listing descriptions.";
    if (PII_EMAIL.test(text)) return "Email addresses are not allowed in listing descriptions.";
    const lower = text.toLowerCase();
    if (companyName.trim().length > 3 && lower.includes(companyName.trim().toLowerCase()))
      return "The business name should not appear in listing descriptions.";
    const nameParts = ownerName.trim().split(/\s+/).filter((p) => p.length > 2);
    for (const part of nameParts) {
      if (lower.includes(part.toLowerCase()))
        return "The seller's name should not appear in listing descriptions.";
    }
    return null;
  };

  // Seller-details validation — only when the fields are hand-typed (picking a
  // registered seller or being the seller yourself fills them from records).
  const sellerEditable = !isSeller && !selectedSellerId;
  const sellerNameError = sellerEditable ? nameIssue(sellerName, "Seller name") : null;
  const sellerEmailError = sellerEditable ? emailIssue(sellerEmail, "Seller email") : null;
  const sellerPhoneError = sellerEditable ? phoneIssue(sellerPhone) : null;

  const piiOverview        = detectPii(overview,        title, sellerName);
  const piiDisplayOverview = detectPii(displayOverview, title, sellerName);
  const piiNotes           = detectPii(additionalNotes, title, sellerName);
  const hasPiiErrors       = !!(piiOverview || piiDisplayOverview || piiNotes);
  // ─────────────────────────────────────────────────────────────────────────

  const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);
  const [states, setStates] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);

  // Pending names from zip lookup waiting for dropdown options to load
  const [pendingStateName, setPendingStateName] = useState<string | null>(null);
  const [pendingCityName, setPendingCityName]   = useState<string | null>(null);
  const [zipLookupStatus, setZipLookupStatus]   = useState<"idle" | "loading" | "found" | "notfound">("idle");

  // Pending location ids from a lead prefill, applied once their dropdowns load
  const [pendingStateId, setPendingStateId] = useState<number | null>(null);
  const [pendingCityId, setPendingCityId]   = useState<number | null>(null);
  const [pendingCityRaw, setPendingCityRaw] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: number; name: string }[]>("/lookups/categories").then(setCategories).catch(() => {});
    // TKT-00118: preselect United States here too, the way the listings filters
    // now do. Every business on the platform is a US one, and the state and city
    // selects are gated on a country, so this was a mandatory click before the
    // address could be filled in at all.
    api.get<{ id: number; name: string }[]>("/lookups/countries")
      .then((rows) => {
        setCountries(rows);
        const us = rows.find((c) => c.name === "United States");
        if (!us) return; // no such row — the field just stays empty, as before
        // Functional, and only when the field is still untouched: a Coming Soon
        // lead prefills its own country in the effect below, and an advisor may
        // have picked one while this request was still in flight. Either must
        // win over the default — and returning `current` unchanged also leaves
        // the state/city reset effect alone.
        setCountryId((current) => current || String(us.id));
      })
      .catch(() => {});
    if (isAdminOrAgent) api.get<typeof sellers>("/business-listings/sellers").then(setSellers).catch(() => {});
  }, []);

  // Pre-populate the form from a Coming Soon lead (when published via the pipeline).
  useEffect(() => {
    if (!leadPrefill) return;
    setSourceLeadId(leadPrefill.id);
    if (leadPrefill.title) setTitle(leadPrefill.title);
    if (leadPrefill.website_url) setWebsiteUrl(leadPrefill.website_url);
    if (leadPrefill.industry_category_id) setIndustryCategoryId(String(leadPrefill.industry_category_id));
    if (leadPrefill.business_description) {
      setOverview(leadPrefill.business_description);
      setDisplayOverview(leadPrefill.business_description);
    }
    if (leadPrefill.owner_email) setSellerEmail(leadPrefill.owner_email);
    if (leadPrefill.image_url) {
      setCoverImageData(leadPrefill.image_url);
      setCoverImagePreview(leadPrefill.image_url);
    }
    // Location cascade: set country now; state/city are applied once their lists load.
    if (leadPrefill.country_id) setCountryId(String(leadPrefill.country_id));
    if (leadPrefill.state_id) setPendingStateId(leadPrefill.state_id);
    if (leadPrefill.city_id) setPendingCityId(leadPrefill.city_id);
    else if (leadPrefill.city_raw) setPendingCityRaw(leadPrefill.city_raw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSellerSelect = (sellerId: string) => {
    setSelectedSellerId(sellerId);
    if (!sellerId) { setSellerName(""); setSellerEmail(""); setSellerPhone(""); return; }
    const s = sellers.find((s) => s.id === sellerId);
    if (s) { setSellerName(`${s.first_name} ${s.last_name}`.trim()); setSellerEmail(s.email); setSellerPhone(s.phone || ""); }
  };

  useEffect(() => {
    if (countryId) {
      api.get<{ id: number; name: string }[]>(`/lookups/states?country_id=${countryId}`)
        .then(setStates).catch(() => {});
    } else {
      setStates([]);
    }
    setStateId(""); setCityId(""); setCities([]);
  }, [countryId]);

  // Once states load, apply pending state from zip lookup
  useEffect(() => {
    if (!pendingStateName || states.length === 0) return;
    const match = states.find((s) =>
      s.name.toLowerCase().includes(pendingStateName.toLowerCase()) ||
      pendingStateName.toLowerCase().includes(s.name.toLowerCase())
    );
    if (match) setStateId(String(match.id));
    setPendingStateName(null);
  }, [states, pendingStateName]);

  useEffect(() => {
    if (stateId) {
      api.get<{ id: number; name: string }[]>(`/lookups/cities?state_id=${stateId}`)
        .then(setCities).catch(() => {});
    } else {
      setCities([]);
    }
    setCityId("");
    setCityName("");
  }, [stateId]);

  // Once cities load, apply pending city from zip lookup
  useEffect(() => {
    if (!pendingCityName || cities.length === 0) return;
    const match = cities.find((c) =>
      c.name.toLowerCase().includes(pendingCityName.toLowerCase()) ||
      pendingCityName.toLowerCase().includes(c.name.toLowerCase())
    );
    if (match) setCityId(String(match.id));
    setPendingCityName(null);
  }, [cities, pendingCityName]);

  // Apply pending location ids from a lead prefill once their dropdowns load
  useEffect(() => {
    if (pendingStateId === null || states.length === 0) return;
    if (states.some((s) => s.id === pendingStateId)) setStateId(String(pendingStateId));
    setPendingStateId(null);
  }, [states, pendingStateId]);

  useEffect(() => {
    if (pendingCityId === null || cities.length === 0) return;
    if (cities.some((c) => c.id === pendingCityId)) setCityId(String(pendingCityId));
    setPendingCityId(null);
  }, [cities, pendingCityId]);

  // city_raw is free text — applied after the [stateId] effect has reset cityName
  useEffect(() => {
    if (!pendingCityRaw || !stateId) return;
    setCityName(pendingCityRaw);
    setPendingCityRaw(null);
  }, [cities, pendingCityRaw, stateId]);

  const generateImages = useCallback(async () => {
    const cat = categories.find((c) => c.id === Number(industryCategoryId));
    if (!cat) return;
    setImageGenerating(true);
    setImageOptions([]);
    try {
      // Must go through `api`: the endpoint is authenticated, and a bare fetch()
      // sends no bearer token — every generation 401'd, so no options ever came
      // back. `api` also surfaces the server's own detail (e.g. generation not
      // configured) instead of one catch-all message.
      const data = await api.get<{ images: string[] }>(
        `/image-proxy/listing-image-options?q=${encodeURIComponent(cat.name)}&count=2`,
      );
      setImageOptions(data.images);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Image generation failed. Try again.");
    } finally {
      setImageGenerating(false);
    }
  }, [categories, industryCategoryId, toast]);

  const handleSelectImage = (dataUrl: string) => {
    // Selecting an AI image clears any uploaded file
    setUploadedFile(null);
    setCoverImageData(dataUrl);
    setCoverImagePreview(dataUrl);
  };

  const handleFileUpload = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Maximum 10 MB.");
      return;
    }
    // Uploading clears any AI selection
    setCoverImageData(null);
    setImageOptions([]);
    setUploadedFile(file);
    setCoverImagePreview(URL.createObjectURL(file));
  };

  const clearCoverImage = () => {
    setCoverImageData(null);
    setUploadedFile(null);
    setCoverImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 3) return;
    setZipLookupStatus("loading");
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(zip)}&format=json&addressdetails=1&limit=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      if (!data || data.length === 0) { setZipLookupStatus("notfound"); return; }

      const addr = data[0].address as Record<string, string>;
      const countryName = addr.country || "";
      const stateName   = addr.state || addr.region || addr.province || "";
      const cityName    = addr.city || addr.town || addr.village || addr.municipality || "";

      // Match country
      const countryMatch = countries.find((c) =>
        c.name.toLowerCase().includes(countryName.toLowerCase()) ||
        countryName.toLowerCase().includes(c.name.toLowerCase())
      );
      if (countryMatch) {
        setCountryId(String(countryMatch.id));
        if (stateName) setPendingStateName(stateName);
        if (cityName)  setPendingCityName(cityName);
        setZipLookupStatus("found");
      } else {
        setZipLookupStatus("notfound");
      }
    } catch {
      setZipLookupStatus("notfound");
    }
  }, [countries]);

  const handleSubmit = async (asDraft: boolean) => {
    if (!user) return;
    if (!formRef.current?.reportValidity()) return;
    if (!industryCategoryId) {
      toast.error("Select an industry category.");
      return;
    }
    if (!countryId) {
      toast.error("Select a country.");
      return;
    }
    if (!stateId) {
      toast.error("Select a state.");
      return;
    }
    if (hasPiiErrors) {
      toast.error("Remove contact details or identifying information from the text fields before submitting.");
      return;
    }
    const sellerIssue = sellerNameError ?? sellerEmailError ?? sellerPhoneError;
    if (sellerIssue) {
      toast.error(sellerIssue);
      return;
    }
    setLoading(true);
    const body: BusinessListingCreate = {
      title,
      overview: overview || undefined,
      display_overview: displayOverview || undefined,
      asking_price: parsedAskingPrice(askingPrice) ?? undefined,
      make_offer: isMakeOfferText(askingPrice),
      revenue: revenue ? Number(revenue) : undefined,
      ebitda: ebitda ? Number(ebitda) : undefined,
      address_line: addressLine || undefined,
      zip_code: zipCode || undefined,
      state_id: stateId ? Number(stateId) : undefined,
      city_id: cityId ? Number(cityId) : undefined,
      city_name: !cityId && cityName ? cityName : undefined,
      country_id: countryId ? Number(countryId) : undefined,
      industry_category_id: Number(industryCategoryId),
      website_url: websiteUrl || undefined,
      established_year: establishedYear ? Number(establishedYear) : undefined,
      established_month: establishedMonth ? Number(establishedMonth) : undefined,
      is_sba_approved: isSbaApproved,
      seller_financing: isSellerFinancing,
      additional_notes: additionalNotes || undefined,
      seller_name: sellerName.trim() || undefined,
      seller_email: sellerEmail.trim() || undefined,
      seller_phone: sellerPhone.trim() || undefined,
      seller_id: selectedSellerId || undefined,
      // Published from a Coming Soon lead: the API retires the lead once this
      // listing goes live — now for an admin, on approval for an advisor.
      source_lead_id: sourceLeadId || undefined,
      listing_status_id: asDraft ? 1 : (isAdmin ? 3 : 2),
      // Only ever persist a short URL here (e.g. a lead's blob image). An
      // AI-generated cover arrives as a multi-MB `data:` URI — that goes to blob
      // storage via /image-base64 after creation, never inline into the column:
      // every endpoint selecting listing_image_url would then ship those bytes.
      listing_image_url:
        coverImageData && !coverImageData.startsWith("data:") ? coverImageData : undefined,
      ...(advisorChecklist
        ? {
            advisor_owner_connected: advisorChecklist.owner_connected,
            advisor_due_diligence_completed: advisorChecklist.due_diligence_completed,
            advisor_nda_signed: advisorChecklist.nda_signed,
            advisor_checklist_notes: advisorChecklist.notes ?? undefined,
          }
        : {}),
    };
    try {
      const created = await api.post<BusinessListing>("/business-listings/", body);
      // If user uploaded a file, send it to the image endpoint after creation
      if (uploadedFile) {
        try {
          await api.upload(`/business-listings/${created.id}/image`, uploadedFile);
        } catch {
          toast.error("Listing created but image upload failed. You can re-upload from the listing page.");
        }
      } else if (coverImageData?.startsWith("data:")) {
        // AI-generated cover — /image-base64 decodes it and stores a blob URL.
        try {
          await api.post(`/business-listings/${created.id}/image-base64`, { image_data: coverImageData });
        } catch {
          toast.error("Listing created but the cover image failed to save. You can re-add it from the listing page.");
        }
      }
      navigate(`/listings/${created.id}`);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-background">
        <div className="text-center">
          <Store className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">Sign in to create a listing</p>
        </div>
      </div>
    );
  }

  // ── Derived preview values ──────────────────────────────────────────
  const previewCategory = categories.find((c) => c.id === Number(industryCategoryId));
  const previewCountry  = countries.find((c) => c.id === Number(countryId));
  const previewState    = states.find((s) => s.id === Number(stateId));
  const previewCity     = cities.find((c) => c.id === Number(cityId));
  const locationParts   = [previewCity?.name || cityName, previewState?.name, previewCountry?.name].filter(Boolean);
  const estLabel = establishedYear
    ? `Est. ${establishedMonth ? MONTHS[Number(establishedMonth) - 1] + " " : ""}${establishedYear}`
    : null;
  const _parsedPrice = parsedAskingPrice(askingPrice);
  const _isMakeOffer = isMakeOfferText(askingPrice);
  const cfMultiple = (_parsedPrice && ebitda && Number(ebitda) > 0)
    ? `${(_parsedPrice / Number(ebitda)).toFixed(2)}x`
    : null;

  const metrics = [
    revenue      && { label: "Annual Revenue", value: fmtCompact(revenue),                                       color: GOLD  },
    ebitda       && { label: "Adj. EBITDA",    value: fmtCompact(ebitda),                                        color: GREEN },
    cfMultiple   && { label: "C.F. Multiple",  value: cfMultiple,                                                color: GREEN },
    askingPrice  && { label: "Asking Price",   value: _isMakeOffer ? askingPrice : fmtCompact(String(_parsedPrice)), color: RED },
  ].filter(Boolean) as { label: string; value: string; color: string }[];

  return (
    /* Regular interior page (was a full-screen modal overlay): scrolls with
       the whole page like every other shell page; the live preview sticks
       beside the form while scrolling. */
    <div className="w-full">
      <div className="w-full space-y-5 px-4 py-6 sm:px-6">

        {/* ── Header — same Dark · Gold hero as the other interior pages ── */}
        <section className="public-hero-shell rounded-[1.85rem] px-6 py-7 shadow-glow sm:px-8">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
          <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="public-pill">
                <FileText className="h-3.5 w-3.5" />
                Listing Builder
              </div>
              <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
                Create <span className="text-gradient">New Listing</span>
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                Fill in the details. See the live preview on the right.
              </p>
            </div>
            <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setTitle("Miami Commercial Cleaning Co.");
                setOverview("Established in 1998, this full-service commercial cleaning business serves over 120 recurring corporate, medical, and retail clients across the Greater Miami area. The business operates with a trained team of 18 full-time employees and a fully equipped fleet of 6 branded vehicles. Revenue is highly predictable with 94% of clients on annual contracts. The owner is retiring and is willing to provide a 90-day transition to ensure continuity. Strong systems, established brand, and motivated buyer support make this a turnkey opportunity.");
                setDisplayOverview("Profitable commercial cleaning business with 25+ years of operation, 120+ recurring clients, and $1M+ annual revenue. Turnkey with full staff and fleet.");
                setAskingPrice("2000000");
                setRevenue("3000000");
                setEbitda("1000000");
                setAddressLine("450 C Street, Suite 200");
                setZipCode("33101");
                setEstablishedYear("1998");
                setEstablishedMonth("3");
                setWebsiteUrl("https://miamicleaningco.com");
                setIsSbaApproved(true);
                setIsSellerFinancing(true);
                setAdditionalNotes("Owner willing to carry 15% seller note. Lease on primary facility runs through 2028 with two 5-year renewal options. All equipment owned outright with no liens.");
              }}
              className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
            >
              Load Example
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
          </div>
        </section>

        {/* ── Body ── */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">

          {/* LEFT: form */}
          <div className="min-w-0 flex-1">
            <form ref={formRef} id="create-form" noValidate className="space-y-4">

              {/* Business profile */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Business Profile</p>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Business title *</Label>
                    <Input placeholder="Downtown Coffee Shop" value={title} onChange={(e) => setTitle(e.target.value)} required className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Industry category *</Label>
                    <SearchableSelect
                      value={industryCategoryId}
                      onChange={setIndustryCategoryId}
                      options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                      placeholder="Select category"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Website URL</Label>
                    <Input type="url" placeholder="https://example.com" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Established year</Label>
                    <Input type="number" min="1800" max="2100" placeholder="2020" value={establishedYear} onChange={(e) => setEstablishedYear(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Established month</Label>
                    <Input type="number" min="1" max="12" placeholder="1–12" value={establishedMonth} onChange={(e) => setEstablishedMonth(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-amber-700">
                    <input type="checkbox" checked={isSbaApproved} onChange={(e) => setIsSbaApproved(e.target.checked)} className="h-4 w-4 rounded accent-primary" />
                    SBA Pre-Approved
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-green-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-green-700">
                    <input type="checkbox" checked={isSellerFinancing} onChange={(e) => setIsSellerFinancing(e.target.checked)} className="h-4 w-4 rounded accent-primary" />
                    Seller Financing
                  </label>
                </div>
              </section>

              {/* Seller Details */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Seller Details</p>
                {isAdminOrAgent && (
                  <div className="mb-4 space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Select registered seller</Label>
                    <select value={selectedSellerId} onChange={(e) => handleSellerSelect(e.target.value)} className={selectCls}>
                      <option value="">Choose a seller or enter manually</option>
                      {sellers.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.email})</option>)}
                    </select>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Seller name *</Label>
                    <Input value={sellerName} onChange={(e) => setSellerName(e.target.value)} required maxLength={60} disabled={isSeller || Boolean(selectedSellerId)} className="h-10 rounded-xl" />
                    {sellerName.trim() !== "" && sellerNameError && (
                      <p className="text-xs font-medium text-destructive">{sellerNameError}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Seller email *</Label>
                    <Input type="email" value={sellerEmail} onChange={(e) => setSellerEmail(e.target.value)} required disabled={isSeller || Boolean(selectedSellerId)} className="h-10 rounded-xl" />
                    {sellerEmail.trim() !== "" && sellerEmailError && (
                      <p className="text-xs font-medium text-destructive">{sellerEmailError}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Seller phone</Label>
                    <Input type="tel" value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} maxLength={20} disabled={isSeller || Boolean(selectedSellerId)} className="h-10 rounded-xl" />
                    {sellerPhoneError && (
                      <p className="text-xs font-medium text-destructive">{sellerPhoneError}</p>
                    )}
                  </div>
                </div>
              </section>

              {/* Cover Image */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Cover Image</p>
                    <p className="text-[0.65rem] text-muted-foreground/60 mt-0.5">Generate with AI or upload your own — optional</p>
                  </div>
                  {coverImagePreview && (
                    <button type="button" onClick={clearCoverImage}
                      className="flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-[0.65rem] text-red-500 hover:bg-red-50 transition-colors">
                      <X className="h-3 w-3" /> Remove
                    </button>
                  )}
                </div>

                {/* Selected / uploaded preview */}
                {coverImagePreview && (
                  <div className="mb-3 overflow-hidden rounded-xl border-2 border-primary relative">
                    <img src={coverImagePreview} alt="Cover" className="h-40 w-full object-cover" />
                    {uploadedFile && (
                      <span className="absolute top-2 left-2 bg-gold-gradient text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full">
                        Uploaded
                      </span>
                    )}
                    {coverImageData && !uploadedFile && (
                      <span className="absolute top-2 left-2 bg-gold-gradient text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full">
                        AI Generated
                      </span>
                    )}
                  </div>
                )}

                {imageGenerating ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/50 bg-gray-50 py-10">
                    <Loader2 className="h-8 w-8 animate-spin text-primary/50" />
                    <span className="text-xs text-muted-foreground">Generating 2 options with AI…</span>
                    <span className="text-[0.65rem] text-muted-foreground/50">This may take ~30s</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* AI options grid (hidden once upload is chosen) */}
                    {imageOptions.length > 0 && !uploadedFile && (
                      <div className="grid grid-cols-2 gap-2">
                        {imageOptions.map((url, i) => {
                          const isSelected = coverImagePreview === url;
                          return (
                            <div key={i} className="relative cursor-pointer" onClick={() => handleSelectImage(url)}>
                              <div className={`overflow-hidden rounded-xl border-2 transition-all ${isSelected ? "border-primary ring-2 ring-primary/20" : "border-transparent hover:border-primary/40"}`}>
                                <img src={url} alt={`Option ${i + 1}`} className="h-32 w-full object-cover" />
                              </div>
                              <div className="absolute top-2 left-2">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectImage(url)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-5 w-5 cursor-pointer rounded accent-primary shadow-md"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Generate / Regenerate button — AI needs a category to
                        prompt with, but uploading your own never did, so this is
                        disabled on its own rather than hiding the whole block. */}
                    <button type="button" onClick={generateImages} disabled={imageGenerating || !!uploadedFile || !industryCategoryId}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border/50 bg-gray-50 py-3 text-xs text-muted-foreground transition-colors hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      {imageOptions.length > 0 && !uploadedFile ? (
                        <><RefreshCw className="h-3.5 w-3.5" /> Regenerate options</>
                      ) : (
                        <><span>🎨</span> Generate cover image with AI</>
                      )}
                    </button>
                    {!industryCategoryId && (
                      <p className="text-center text-[0.65rem] text-muted-foreground/60">Select a category first to generate cover images with AI</p>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 border-t border-border/40" />
                      <span className="text-[0.65rem] font-medium text-muted-foreground/50 uppercase tracking-widest">or</span>
                      <div className="flex-1 border-t border-border/40" />
                    </div>

                    {/* Upload button */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageGenerating}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/50 bg-gray-50 py-3 text-xs text-muted-foreground transition-colors hover:bg-gray-100 hover:border-primary/30 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {uploadedFile ? (
                        <span className="text-primary font-medium">{uploadedFile.name}</span>
                      ) : (
                        "Upload your own photo (JPG, PNG, WEBP · max 10 MB)"
                      )}
                    </button>
                  </div>
                )}
              </section>

              {/* Overview */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Overview &amp; Description</p>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Business description</Label>
                    <div className={piiOverview ? "rounded-xl ring-2 ring-amber-400" : ""}>
                      <Textarea rows={8} placeholder="Describe history, operations, team, and growth opportunities…" value={overview} onChange={(e) => setOverview(e.target.value)} className="rounded-xl resize-none text-sm" />
                    </div>
                    {piiOverview && (
                      <p className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <span className="font-bold">⚠</span> {piiOverview}
                      </p>
                    )}
                  </div>
                  {isAdminOrAgent && (
                    <div className="space-y-1.5">
                      <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Public display description *</Label>
                      <div className={piiDisplayOverview ? "rounded-xl ring-2 ring-amber-400" : ""}>
                        <Textarea rows={6} placeholder="Concise version shown to buyers and guests…" value={displayOverview} onChange={(e) => setDisplayOverview(e.target.value)} required className="rounded-xl resize-none text-sm" />
                      </div>
                      {piiDisplayOverview && (
                        <p className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                          <span className="font-bold">⚠</span> {piiDisplayOverview}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </section>

              {/* Financials */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Financial Metrics</p>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Asking price (USD)</Label>
                    <Input
                      type="text"
                      placeholder="e.g. 1500000 or 'Make Offer'"
                      value={askingPrice}
                      onChange={(e) => setAskingPrice(e.target.value)}
                      className="h-10 rounded-xl"
                    />
                    <p className="text-[0.65rem] text-muted-foreground/60">Enter a dollar amount or text like "Make Offer" / "Best Offer"</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Annual revenue (USD) *</Label>
                    <Input type="number" min="0" step="0.01" placeholder="0.00" value={revenue} onChange={(e) => setRevenue(e.target.value)} required className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">EBITDA (USD) *</Label>
                    <Input type="number" min="0" step="0.01" placeholder="0.00" value={ebitda} onChange={(e) => setEbitda(e.target.value)} required className="h-10 rounded-xl" />
                  </div>
                </div>
              </section>

              {/* Location */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Location</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Street address</Label>
                    <Input placeholder="123 Main St" value={addressLine} onChange={(e) => setAddressLine(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      Zip / Postal code
                      {zipLookupStatus === "found" && <span className="ml-2 text-emerald-600 normal-case font-normal">✓ Location auto-filled</span>}
                      {zipLookupStatus === "notfound" && <span className="ml-2 text-amber-500 normal-case font-normal">Not found — fill manually</span>}
                    </Label>
                    <div className="relative">
                      <Input
                        placeholder="e.g. 33101, SW1A, 110001"
                        value={zipCode}
                        onChange={(e) => { setZipCode(e.target.value); setZipLookupStatus("idle"); }}
                        onBlur={(e) => lookupZip(e.target.value.trim())}
                        className="h-10 rounded-xl pr-9"
                      />
                      {zipLookupStatus === "loading" && (
                        <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Country *</Label>
                    <SearchableSelect
                      value={countryId}
                      onChange={setCountryId}
                      options={countries.map((c) => ({ value: String(c.id), label: c.name }))}
                      placeholder="Select country"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">State *</Label>
                    <SearchableSelect
                      value={stateId}
                      onChange={setStateId}
                      options={states.map((s) => ({ value: String(s.id), label: s.name }))}
                      placeholder="Select state"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                      disabled={!countryId}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">City</Label>
                    <SearchableSelect
                      value={cityId}
                      onChange={(v) => { setCityId(v); if (v) setCityName(""); }}
                      options={cities.map((c) => ({ value: String(c.id), label: c.name }))}
                      placeholder="Select city"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                      disabled={!stateId || !!cityName}
                    />
                    {stateId && (
                      <Input
                        placeholder="City not in list? Type it here"
                        value={cityName}
                        onChange={(e) => { setCityName(e.target.value); if (e.target.value) setCityId(""); }}
                        className="h-9 rounded-xl text-sm"
                      />
                    )}
                  </div>
                </div>
              </section>

              {/* Notes */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Additional Notes</p>
                <div className={piiNotes ? "rounded-xl ring-2 ring-amber-400" : ""}>
                  <Textarea rows={3} placeholder="Optional context, constraints, transfer notes, or timeline details…" value={additionalNotes} onChange={(e) => setAdditionalNotes(e.target.value)} className="rounded-xl resize-none text-sm" />
                </div>
                {piiNotes && (
                  <p className="mt-2 flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <span className="font-bold">⚠</span> {piiNotes}
                  </p>
                )}
              </section>
            </form>
          </div>

          {/* RIGHT: live preview — full natural height, scrolls with the page */}
          <div className="hidden w-[400px] shrink-0 lg:block">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-muted-foreground">Live Preview — what buyers will see</span>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border/50 bg-white shadow-md">
              {/* Cover image */}
              {coverImagePreview && (
                <img src={coverImagePreview} alt="Cover" className="h-32 w-full object-cover" />
              )}
              {/* Navy header */}
              <div className="px-5 py-4 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 100%)` }}>
                {previewCategory && (
                  <span className="mb-2 inline-block rounded-full bg-white/20 px-2.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-widest text-white/90">
                    {previewCategory.name}
                  </span>
                )}
                <h2 className="text-lg font-extrabold leading-tight text-white">
                  {title || <span className="italic text-white/40">Business title…</span>}
                </h2>
                {locationParts.length > 0 && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs text-white/70">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {locationParts.join(", ")}
                  </div>
                )}
                {estLabel && (
                  <div className="mt-1 text-xs text-white/60">{estLabel}</div>
                )}
                {(isSbaApproved || isSellerFinancing) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {isSbaApproved && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/25 px-2 py-0.5 text-[0.6rem] font-bold text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" /> SBA Pre-Approved
                      </span>
                    )}
                    {isSellerFinancing && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-400/25 px-2 py-0.5 text-[0.6rem] font-bold text-blue-400">
                        <CheckCircle2 className="h-3 w-3" /> Seller Financing
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Metrics bar */}
              {metrics.length > 0 && (
                <div className={`grid divide-x divide-border/50 border-b border-border/50`}
                  style={{ gridTemplateColumns: `repeat(${metrics.length}, 1fr)` }}>
                  {metrics.map((m) => (
                    <div key={m.label} className="flex flex-col items-center py-3 px-2">
                      <span className="text-base font-extrabold" style={{ color: m.color }}>{m.value}</span>
                      <span className="mt-0.5 text-[0.55rem] font-bold uppercase tracking-widest text-muted-foreground">{m.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Overview */}
              <div className="p-4">
                {(overview || displayOverview) ? (
                  <>
                    <div className="mb-1 flex items-center gap-1.5">
                      <div className="h-3.5 w-0.5 rounded-full bg-primary" />
                      <span className="text-[0.6rem] font-bold uppercase tracking-widest text-muted-foreground">The Opportunity</span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-gray-700 line-clamp-4">
                      {overview || displayOverview}
                    </p>
                  </>
                ) : (
                  <p className="text-xs italic text-muted-foreground/50">Business description will appear here…</p>
                )}

                {/* Financial summary card */}
                {askingPrice && (
                  <div className="mt-4 overflow-hidden rounded-xl shadow" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 100%)` }}>
                    <div className="p-4 text-center">
                      <p className="text-[0.55rem] font-bold uppercase tracking-widest text-white/50 mb-1">Asking Price</p>
                      <p className="text-2xl font-extrabold" style={{ color: GOLD }}>{_isMakeOffer ? askingPrice : fmtCompact(String(_parsedPrice))}</p>
                      {cfMultiple && <p className="mt-0.5 text-xs text-white/60">{cfMultiple} Cash Flow Multiple</p>}
                    </div>
                    {(revenue || ebitda) && (
                      <div className="grid grid-cols-2 divide-x divide-white/10 border-t border-white/10">
                        {revenue && (
                          <div className="py-3 text-center">
                            <p className="text-[0.55rem] font-bold uppercase tracking-widest text-white/50">Revenue</p>
                            <p className="mt-0.5 text-sm font-extrabold" style={{ color: GOLD }}>{fmtCompact(revenue)}</p>
                          </div>
                        )}
                        {ebitda && (
                          <div className="py-3 text-center">
                            <p className="text-[0.55rem] font-bold uppercase tracking-widest text-white/50">EBITDA</p>
                            <p className="mt-0.5 text-sm font-extrabold" style={{ color: "#86efac" }}>{fmtCompact(ebitda)}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Quick details */}
                {(websiteUrl || additionalNotes) && (
                  <div className="mt-4 space-y-2 rounded-xl border border-border/50 bg-gray-50 p-3">
                    <p className="text-[0.6rem] font-bold uppercase tracking-widest text-muted-foreground">Details</p>
                    {websiteUrl && (
                      <div className="flex items-center gap-1.5 text-xs text-primary">
                        <Globe className="h-3 w-3 shrink-0" />
                        <span className="truncate">{websiteUrl}</span>
                      </div>
                    )}
                    {additionalNotes && (
                      <p className="text-xs text-gray-600 line-clamp-2">{additionalNotes}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer actions ── */}
        <div className="flex flex-col gap-2 rounded-2xl border border-border/60 bg-card px-5 py-4 shadow-sm">
          {hasPiiErrors && (
            <p className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 font-medium">
              <span className="text-base leading-none">⚠</span>
              One or more text fields contain contact details or identifying information that must be removed before submitting.
            </p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-amber-600">Draft</span> — saved privately &nbsp;|&nbsp;
              <span className="font-semibold text-emerald-600">Submit</span> — {isAdmin ? "published immediately" : "sent for review"}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleSubmit(true)} disabled={loading || hasPiiErrors} className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                <Save className="h-4 w-4" />
                {loading ? "Saving…" : "Save as Draft"}
              </Button>
              <Button onClick={() => handleSubmit(false)} disabled={loading || hasPiiErrors} className="rounded-xl disabled:opacity-50">
                <CheckCircle2 className="h-4 w-4" />
                {loading ? "Submitting…" : (isAdmin ? "Submit" : "Submit for Review")}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
